import { Injectable } from '@angular/core';

/** Target render width per page in CSS pixels — multiplied by the device pixel ratio below and oversampled so the page stays crisp at fit and when zoomed */
const PAGE_RENDER_WIDTH = 2400;
/** Maximum render scale, guarding against oversized canvases for already-large pages */
const MAX_RENDER_SCALE = 6;
/** Cap on the device-pixel-ratio factor, so high-DPR phones don't blow up the canvas for no visible gain */
const MAX_DEVICE_PIXEL_RATIO = 2;
/** JPEG quality used for the rendered page images */
const JPEG_QUALITY = 0.85;

/** The result of rendering a PDF document to page images. */
export interface RenderedPdf {
  /** Raw PDF bytes, e.g. to back a download link */
  bytes: ArrayBuffer;
  /** One object URL per page (JPEG), in document order */
  pageUrls: string[];
  /** Aspect ratio (width / height) per page, in document order */
  pageAspects: number[];
}

/**
 * Loads a PDF and renders each page to an image. Browser-only: pdf.js is imported dynamically so it is
 * never bundled into the server build. The caller owns the returned object URLs and must revoke them.
 */
@Injectable({ providedIn: 'root' })
export class PdfRenderService {
  // Methods
  /**
   * Fetches the PDF and renders every page to a JPEG object URL.
   * @param src - Absolute PDF URL
   * @param headers - Request headers (e.g. `channel`/`language`) the CMS download endpoint requires
   * @param signal - Optional abort signal to cancel the request when a newer load supersedes this one
   * @returns The raw bytes plus one object URL and aspect ratio per page
   */
  async render(src: string, headers: Record<string, string>, signal?: AbortSignal): Promise<RenderedPdf> {
    const response = await fetch(src, { headers, signal });
    if (!response.ok) throw new Error(`PDF request failed with status ${response.status}`);
    const bytes = await response.arrayBuffer();

    // Legacy build: the modern one needs APIs like Map.getOrInsertComputed (Chrome 145, iOS 26.2) without polyfills.
    const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
    // The asset name is stable and cached for a year, so the version query keeps worker and API in sync after updates.
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(`assets/pdf.worker.min.mjs?v=${pdfjs.version}`, document.baseURI).href;
    const pdfDocument = await pdfjs.getDocument({
      data: bytes.slice(0),
      // JPEG 2000/JBIG2 decoders and ICC color management ship as wasm since pdf.js 5; the worker fetches them itself.
      wasmUrl: new URL('assets/pdfjs/wasm/', document.baseURI).href,
      iccUrl: new URL('assets/pdfjs/iccs/', document.baseURI).href,
      useWorkerFetch: true
    }).promise;

    const devicePixelRatio = Math.min(MAX_DEVICE_PIXEL_RATIO, typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1);

    const pageUrls: string[] = [];
    const pageAspects: number[] = [];
    for (let pageNumber = 1; pageNumber <= pdfDocument.numPages; pageNumber++) {
      const page = await pdfDocument.getPage(pageNumber);
      const baseViewport = page.getViewport({ scale: 1 });
      const scale = Math.min(MAX_RENDER_SCALE, (PAGE_RENDER_WIDTH * devicePixelRatio) / baseViewport.width);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      await page.render({ canvas, viewport }).promise;
      pageUrls.push(await this.canvasToObjectUrl(canvas));
      pageAspects.push(baseViewport.width / baseViewport.height);
      page.cleanup();
    }

    return { bytes, pageUrls, pageAspects };
  }

  /**
   * Renders a canvas to a JPEG object URL, falling back to a data URL where `toBlob` is unavailable.
   * @param canvas - The canvas holding the rendered page
   * @returns An object (or data) URL for the page image
   */
  private canvasToObjectUrl(canvas: HTMLCanvasElement): Promise<string> {
    return new Promise<string>((resolve) => {
      canvas.toBlob(
        (blob) => resolve(blob ? URL.createObjectURL(blob) : canvas.toDataURL('image/jpeg', JPEG_QUALITY)),
        'image/jpeg',
        JPEG_QUALITY
      );
    });
  }
}
